import * as Sentry from '@sentry/react';
import ms from 'ms';
import { proxy, subscribe } from 'valtio';
import { gql } from '@soundxyz/gql-string';
import { getFromList } from '@soundxyz/utils';
import { assertAddress } from '@soundxyz/utils/address';
import { authUserPersistenceStore } from '@context/auth/persistence';
import type { PlaylistInput } from '@graphql/reactQuery';
import {
  CollectorStatusDocument,
  fetchQuery,
  GetPlaylistDocument,
  GetPlaylistTrackIdsDocument,
  GraphQLReactQuery,
  ListeningPartyReleaseInfoDocument,
  PlayerTrackInfoDocument,
  ReleasesArePresavedDocument,
} from '@graphql/reactQuery';
import {
  releaseListeningPartyMerkleQueryFn,
  releaseListeningPartyMerkleQueryKey,
} from '@hooks/play/helpers';
import { getReleasePresavedBatchedKeys } from '@hooks/useIsReleasePresaved';
import {
  computeAudioUrl,
  parseReleaseTrackAudioInfo,
  parseTrackAudioInfo,
} from '@hooks/useTrackAudioInfo';
import { removeTrackById } from '@store/audio/AudioQueue';
import { isHex } from '@utils/hex';
import { store } from './Audio';
import { loadActiveTrack } from './AudioController';
import { AudioMeta, setActivePlaylist } from './AudioMeta';
import {
  addTrackToQueue,
  AudioQueue,
  clearPlaybackSequence,
  setActiveTrack,
  setUpNextTrackIds,
} from './AudioQueue';
import { PersistentAudio } from './Persistence';

// How many tracks ahead to fetch
const PREFETCH_THRESHOLD = 2;

/*#__PURE__*/ gql(/* GraphQL */ `
  query GetPlaylist($input: PlaylistInput!) {
    playlist: legacyPlaylist(input: $input) {
      id
      tracks {
        id
        releaseId
        artistId
      }
    }
  }

  query GetPlaylistTrackIds($playlistId: UUID!) {
    playlistV2(id: $playlistId) {
      id
      trackIds
      releaseIds
    }
  }
`);

export const addTrackAudioToQueue = async ({
  trackId,
  position,
}: {
  trackId: string;
  position?: number;
}) => {
  addTrackToQueue({ trackId, position });
};

export const addPlaylistAudioToQueue = async (input: PlaylistInput) => {
  const { data, errors: error } = await fetchQuery(GetPlaylistDocument, {
    variables: { input },
    staleTime: Infinity,
  });
  if (error) {
    Sentry.captureException(error);
    return [];
  }
  const playlistTracks = data?.playlist?.tracks;
  if (playlistTracks) {
    const trackIds = playlistTracks
      .map(track => track.id)
      .filter(trackId => trackId !== AudioMeta.activeTrackId);
    setUpNextTrackIds(trackIds);
    return playlistTracks;
  }
  return [];
};

// Prefetches audio to store file in browser cache
export const prefetchAudioFile = ({ url }: { url: string }) => {
  // Check if already present
  if (store.audioFiles.has(url)) return;
  store.audioFiles.add(url);

  new Audio(url);
};

if (typeof window !== 'undefined') {
  subscribe(
    proxy({ audioQueue: AudioQueue, audioEngineHTML5: PersistentAudio.audioEngineHTML5 }),
    () => {
      if (
        !AudioQueue.active ||
        !PersistentAudio.isReady ||
        !PersistentAudio.audioEngineHTML5.playing
      )
        return;

      // Prefetch tracks from active index up to the prefetch threshold
      const tracksToFetch = AudioQueue.playbackTrackIds.slice(
        AudioQueue.active.playbackIndex,
        AudioQueue.active.playbackIndex + PREFETCH_THRESHOLD,
      );
      const fetcher = async () => {
        await Promise.allSettled([
          ...tracksToFetch.map(async trackId => {
            const response = await fetchQuery(PlayerTrackInfoDocument, {
              variables: {
                trackId,
              },
              staleTime: Infinity,
              cacheTime: Infinity,
            });

            const track = parseTrackAudioInfo(response.data.track);

            if (!track) {
              // track is not playable so remove it from queue
              removeTrackById(trackId);
              return;
            }

            const audioUrl = computeAudioUrl(track.audio);

            prefetchAudioFile({ url: audioUrl });
          }),
        ]);
      };

      fetcher();
    },
  );
}

export const setPlaylistUpNext = async (playlistId: string, trackId?: string) => {
  const { data } = await fetchQuery(GetPlaylistTrackIdsDocument, {
    variables: { playlistId },
    /**
     * If the target playlist is already active, we can re-use existing cache,
     * otherwise we want to refetch in case between changing playlists the previous playlist
     * got updated
     */
    staleTime: playlistId !== AudioMeta.activePlaylistId ? 0 : Infinity,
  });

  const playlist = data?.playlistV2;
  if (!playlist || !playlist.trackIds) return;

  setActivePlaylist(playlistId, playlist.releaseIds);

  clearPlaybackSequence();
  setUpNextTrackIds(playlist.trackIds);

  const index = trackId ? AudioQueue.playbackTrackIds.indexOf(trackId) : 0;
  if (index < 0) return;
  setActiveTrack(index);
  loadActiveTrack();
};

export async function queryAudioFromTrack({ trackId }: { trackId: string }) {
  const response = await fetchQuery(PlayerTrackInfoDocument, {
    variables: {
      trackId,
    },
    staleTime: Infinity,
    cacheTime: Infinity,
  });

  const release = parseReleaseTrackAudioInfo(response.data.track?.release);

  const listeningParty = release?.listeningParty;

  const isListeningPartyInTheFuture =
    listeningParty != null &&
    /**
     * We remove 3 seconds from the timestamp checks to account for race-conditions on the triggers
     */
    listeningParty - 3_000 > Date.now();

  if (release && isListeningPartyInTheFuture) {
    const user = authUserPersistenceStore.state.value?.data.authUser;
    if (user) {
      const batchedPresavedKeys = getReleasePresavedBatchedKeys({
        releaseId: release.id,
      });

      const [isCollector, isPresaved] = await Promise.all([
        fetchQuery(CollectorStatusDocument, {
          variables: {
            userId: user.id,
            releaseId: release.id,
          },
          staleTime: ms('5 minutes'),
          retry: 5,
        }).then(v => !!v.data.user?.collectedReleases.edges.length),
        release.spotifyTrackId
          ? fetchQuery(
              ReleasesArePresavedDocument,
              batchedPresavedKeys
                ? {
                    variables: {
                      releaseIds: batchedPresavedKeys,
                    },
                    /**
                     * We want to re-use possible data cached from React Query hooks
                     */
                    staleTime: ms('5 minutes'),
                  }
                : {
                    variables: {
                      releaseIds: [release.id],
                    },
                    staleTime: 0,
                  },
            ).then(
              v =>
                v.data.userReleaseStatus.find(
                  releaseStatus => releaseStatus.releaseId === release.id,
                )?.isPreSaved ?? false,
            )
          : false,
      ]);

      if (isCollector || isPresaved) {
        /**
         * Continue playback, user is collector or presaver of the release, listening party does not apply
         */
      } else {
        /**
         * User is not collector, we now have to check for presale early access
         */

        const releasePresaleMerkleTreeRoot = await fetchQuery(ListeningPartyReleaseInfoDocument, {
          variables: {
            releaseId: release.id,
          },
          retry: 5,

          /**
           * We want to re-use possible data cached from React Query hooks
           */
          staleTime: ms('10 minutes'),
        }).then(queryData =>
          getFromList(queryData.data.release?.saleSchedules, v => {
            const startTime = new Date(v.startTime).getTime();

            /**
             * We only want past presales
             */
            return (
              v.merkleTreeRoot != null &&
              isHex(v.merkleTreeRoot) &&
              startTime < listeningParty &&
              Date.now() >= startTime &&
              v.merkleTreeRoot
            );
          }),
        );

        /**
         * Presale exists, check if user is allowlisted
         */
        if (releasePresaleMerkleTreeRoot) {
          assertAddress(user.publicAddress);
          const userAddress = user.publicAddress;

          const isAllowlisted = await GraphQLReactQuery.fetchQuery({
            queryKey: releaseListeningPartyMerkleQueryKey({
              merkleTreeRoot: releasePresaleMerkleTreeRoot,
              userAddress,
            }),
            queryFn: () =>
              releaseListeningPartyMerkleQueryFn({
                merkleTreeRoot: releasePresaleMerkleTreeRoot,
                userAddress,
              }),
            retry: 5,

            /**
             * We always want to re-use possible cached data, merkle root changes so cache key would be different if allowlist/not-allowlisted
             */
            staleTime: Infinity,
            cacheTime: Infinity,
          });

          if (isAllowlisted) {
            /**
             * Continue playback, user is allowlisted for past presale
             */
          } else {
            /**
             * Behind listening party, user is not allowlisted for past presale
             */
            return null;
          }
        } else {
          /**
           * Behind listening party, presale does not exists, so only public listening party applies
           */
          return null;
        }
      }
    } else {
      /**
       * Behind listening party, user is not authenticated and listening party is in the future
       */
      return null;
    }
  } else {
    /**
     * Continue playback, listening party does not exist or is in the past
     */
  }

  const track = parseTrackAudioInfo(response.data.track);

  if (!release || !track) {
    // track is not playable so remove it from queue
    removeTrackById(trackId);
    return { errors: response.errors };
  }

  return {
    url: computeAudioUrl(track.audio),
    originalUrl: track.audio.audioOriginal.url,
    release,
  };
}
